import { parseUnits } from 'viem'
import { base } from 'viem/chains'

import { useEarnDepositAutomation } from '~/app/settings/team/upgrade/useEarnDepositAutomation'
import { usePlanDeposit } from '~/app/settings/team/upgrade/usePlanDeposit'
import { useUpgradeState } from '~/app/settings/team/upgrade/useUpgradeState'
import { formatUsdc } from '~/app/settings/team/upgrade/utils'
import {
  Button,
  Copyable,
  List,
  LoadingSpinner,
  Message,
  Tabs,
  TabsContent,
  TokenBalance,
  TokenDisplay,
} from '~/components/component-library'
import { SubscribeDialog } from '~/components/plans/SubscribeDialog'
import { useAuth } from '~/context/AuthProvider'
import { PaidPlanName } from '~/hooks/usePlans'
import { EvmAddress } from '~/utils/address'
import { displayCurrency } from '~/utils/display'
import { BASE_USDC } from '~/utils/vault'

const PREVIEW_DISABLED_REASON = 'Disabled while previewing'

// The ways to unlock the selected plan: deposit into Earn from the org's
// accounts, send USDC to a deposit address when they can't cover it, or
// subscribe. Renders nothing once the plan is held.
export const PlanDepositSection = ({
  planName,
  isPreviewing,
  isOwner,
  upgradeState,
}: {
  planName: PaidPlanName
  isPreviewing: boolean
  isOwner: boolean
  upgradeState: ReturnType<typeof useUpgradeState>
}) => {
  const {
    plan,
    scenario,
    amountNeeded,
    accounts,
    isLoading,
    errorMessage,
    retry,
  } = upgradeState
  const { mutate: deposit, isPending: isDepositing } = usePlanDeposit(plan?.id)
  // Previewing never creates a real automation, and the server only lets
  // owners create one.
  const {
    automation,
    isError: hasAutomationError,
    retry: retryAutomation,
  } = useEarnDepositAutomation({
    enabled: scenario === 'insufficient' && !isPreviewing && isOwner,
  })
  const { setGlobalModalState } = useAuth()

  const usdcAmountText =
    amountNeeded === undefined ? undefined : formatUsdc(amountNeeded)

  const depositButton = (
    <div className="flex justify-end">
      <Button
        onClick={() => deposit()}
        isLoading={isDepositing}
        isDisabled={isPreviewing}
        disabledReason={PREVIEW_DISABLED_REASON}
        eventName="clickedPlanUpgradeDeposit"
        eventProperties={{ plan: planName, scenario }}
      >
        Deposit
      </Button>
    </div>
  )

  const renderDepositAddress = () => {
    if (automation?.accountAddress)
      return (
        <div className="space-y-2">
          <Copyable
            layout="field"
            label="Deposit address on Base"
            value={EvmAddress.format(automation.accountAddress)}
            successMessage="Address copied"
            truncateMiddle
          />
          <p className="text-sm text-secondary">
            Send at least {usdcAmountText} USDC on Base to this address. It’s
            deposited into Earn in your Treasury automatically.
          </p>
        </div>
      )
    if (hasAutomationError)
      return (
        <Message
          variant="danger"
          action={{ label: 'Retry', onClick: retryAutomation }}
        >
          Unable to set up your deposit address.
        </Message>
      )
    return (
      <div className="flex items-center gap-2 text-secondary">
        <LoadingSpinner size="sm" />
        Setting up your deposit address…
      </div>
    )
  }

  const renderScenario = () => {
    if (isLoading)
      return (
        <div className="text-secondary">
          <LoadingSpinner size="lg" />
        </div>
      )
    if (errorMessage)
      return (
        <Message variant="danger" action={{ label: 'Retry', onClick: retry }}>
          {errorMessage}
        </Message>
      )

    switch (scenario) {
      case 'single':
      case 'multi':
        return (
          <div className="space-y-4">
            <List
              label="From"
              items={accounts.map((account) => ({
                key: account.address,
                content: account.name,
                meta: `${formatUsdc(account.depositAmount)} USDC`,
              }))}
            />
            {depositButton}
          </div>
        )
      case 'insufficient':
        return (
          <div className="space-y-6">
            <List
              label="Minimum deposit"
              items={[
                {
                  key: 'minimum-deposit',
                  content: (
                    <TokenDisplay
                      layout="two-line"
                      address={BASE_USDC}
                      chainId={base.id}
                      hideInactiveChainIndicator
                    />
                  ),
                  meta: (
                    <TokenBalance
                      address={BASE_USDC}
                      chainId={base.id}
                      tokenAmount={parseUnits(String(amountNeeded ?? 0), 6)}
                      layout="standard"
                      direction="neutral"
                      size="base"
                      enableCopyAmount
                    />
                  ),
                },
              ]}
            />
            {renderDepositAddress()}
          </div>
        )
      default:
        return null
    }
  }

  // Loading and error states render alone; the tabs only make sense once
  // there's a real shortfall to act on.
  if (
    !plan ||
    isLoading ||
    errorMessage ||
    !scenario ||
    scenario === 'satisfied'
  )
    return renderScenario()

  // Depositing and subscribing are both owner-only on the server.
  if (!isOwner)
    return (
      <Message variant="info">Ask an admin to upgrade your workspace.</Message>
    )

  return (
    <Tabs
      defaultValue="deposit"
      options={[
        { value: 'deposit', display: 'Deposit' },
        { value: 'subscribe', display: 'Subscribe' },
      ]}
    >
      <TabsContent value="deposit">{renderScenario()}</TabsContent>
      <TabsContent value="subscribe" className="space-y-6">
        <div>
          <p>
            Subscribe for{' '}
            {displayCurrency(plan.subscriptionPrice, { decimalPoints: 0 })}
            /year
          </p>
          <p className="text-sm text-secondary">
            Skip Earn and pay an annual invoice instead
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={() =>
              setGlobalModalState({
                isOpen: true,
                content: <SubscribeDialog plan={planName} />,
              })
            }
          >
            Subscribe
          </Button>
        </div>
      </TabsContent>
    </Tabs>
  )
}
